import {
  CUSTOM_SIZE,
  DEFAULT_QUANTITY_DISCOUNT_TIERS,
  REFLECTIVE_SURCHARGE_PER_PANEL,
  SIZE_PRESETS,
  SIZE_TIERS,
  VINYL_COLORS,
} from './constants.js';
import type {
  PatchConfig,
  PriceBreakdown,
  PriceLineItem,
  QuantityDiscountTier,
  Size,
  SizeTier,
} from './types.js';

export interface PricingContext {
  readonly quantityDiscountTiers?: readonly QuantityDiscountTier[];
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatInches(n: number): string {
  return `${n}"`;
}

// Display convention for patch sizes is "short × long" — matches the spec's
// "1" × 4"" labels and reads naturally on horizontal ID patches.
export function formatSizeLabel(size: Size): string {
  const short = Math.min(size.width, size.height);
  const long = Math.max(size.width, size.height);
  return `${formatInches(short)} × ${formatInches(long)}`;
}

// Smallest tier whose bounding box contains the size in either orientation.
// Tier contains size ⇔ max(w,h) ≤ maxLong AND min(w,h) ≤ maxShort.
export function resolveSizeTier(size: Size): SizeTier {
  const longSide = Math.max(size.width, size.height);
  const shortSide = Math.min(size.width, size.height);
  for (const tier of SIZE_TIERS) {
    if (longSide <= tier.maxLong && shortSide <= tier.maxShort) {
      return tier;
    }
  }
  return SIZE_TIERS[SIZE_TIERS.length - 1]!;
}

export function isPresetSize(size: Size): boolean {
  return SIZE_PRESETS.some(
    (p) =>
      (p.width === size.width && p.height === size.height) ||
      (p.width === size.height && p.height === size.width),
  );
}

export function resolveQuantityDiscount(
  quantity: number,
  tiers: readonly QuantityDiscountTier[],
): QuantityDiscountTier {
  for (const tier of tiers) {
    const upper = tier.maxQty ?? Infinity;
    if (quantity >= tier.minQty && quantity <= upper) return tier;
  }
  return tiers[0]!;
}

export function calculatePrice(
  config: PatchConfig,
  ctx: PricingContext = {},
): PriceBreakdown {
  const tier = resolveSizeTier(config.size);
  const basePricePerPanel = tier.basePrice;

  const isCustom = !isPresetSize(config.size);
  const customSurchargeWaived = isCustom && config.quantity >= CUSTOM_SIZE.waiverMinQuantity;
  const customSizeSurchargePerPanel =
    isCustom && !customSurchargeWaived ? CUSTOM_SIZE.surchargePerPanel : 0;

  const vinyl = VINYL_COLORS.find((c) => c.id === config.vinylColorId);
  const reflectiveSurchargePerPanel = vinyl?.reflective ? REFLECTIVE_SURCHARGE_PER_PANEL : 0;

  const perPanel =
    basePricePerPanel + customSizeSurchargePerPanel + reflectiveSurchargePerPanel;
  const subtotalBeforeDiscount = round2(perPanel * config.quantity);

  const discountTiers = ctx.quantityDiscountTiers ?? DEFAULT_QUANTITY_DISCOUNT_TIERS;
  const discountTier = resolveQuantityDiscount(config.quantity, discountTiers);
  const quantityDiscountAmount = round2(
    subtotalBeforeDiscount * (discountTier.discountPercent / 100),
  );

  const total = round2(subtotalBeforeDiscount - quantityDiscountAmount);

  const lineItems: PriceLineItem[] = [
    {
      label: `Base (${formatSizeLabel(config.size)}, ${tier.displayName})`,
      detail: `$${basePricePerPanel.toFixed(2)} × ${config.quantity}`,
      amount: round2(basePricePerPanel * config.quantity),
    },
  ];

  if (isCustom) {
    if (customSurchargeWaived) {
      lineItems.push({
        label: 'Custom size surcharge',
        detail: `Waived (quantity ≥ ${CUSTOM_SIZE.waiverMinQuantity})`,
        amount: 0,
      });
    } else {
      lineItems.push({
        label: 'Custom size surcharge',
        detail: `$${CUSTOM_SIZE.surchargePerPanel.toFixed(2)} × ${config.quantity}`,
        amount: round2(CUSTOM_SIZE.surchargePerPanel * config.quantity),
      });
    }
  }

  if (reflectiveSurchargePerPanel > 0) {
    lineItems.push({
      label: 'Reflective vinyl surcharge',
      detail: `$${REFLECTIVE_SURCHARGE_PER_PANEL.toFixed(2)} × ${config.quantity}`,
      amount: round2(REFLECTIVE_SURCHARGE_PER_PANEL * config.quantity),
    });
  }

  if (quantityDiscountAmount > 0) {
    lineItems.push({
      label: `Quantity discount (${discountTier.discountPercent}%)`,
      detail: `−$${quantityDiscountAmount.toFixed(2)}`,
      amount: -quantityDiscountAmount,
    });
  }

  return {
    basePricePerPanel,
    tierId: tier.id,
    isCustomSize: isCustom,
    customSizeSurchargePerPanel,
    customSizeSurchargeWaived: customSurchargeWaived,
    reflectiveSurchargePerPanel,
    quantity: config.quantity,
    subtotalBeforeDiscount,
    quantityDiscountPercent: discountTier.discountPercent,
    quantityDiscountAmount,
    total,
    currency: 'USD',
    lineItems,
  };
}
